/**
 * usePowerInquiry Hook
 * Manages state and operations for power inquiry search functionality
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  searchPowers,
  searchPowersByReport,
  getAggregates,
  getFilters,
  getPowerDetails,
  exportPowersCsv,
  downloadBlob,
  DATE_TYPES,
  FILTER_TYPES,
  DEFAULT_PAGE_SIZE,
  SORT_FIELDS,
  VIEW_MODES,
} from '../services/oathPowerInquiryService';

/**
 * Hook for managing power inquiry state and operations
 * @param {number} initialAgencyId - Initial agency ID (deprecated, use initialSearchCriteria.agencyIds)
 * @param {Object[]} agencies - User's agencies array
 * @param {Object} initialFilters - Optional initial filters from navigation (e.g., from Dashboard)
 * @param {string} initialFilters.groupFilter - Group filter name (e.g., 'OpenLiability')
 * @param {string[]} initialFilters.specificFilters - Array of specific filter names
 * @param {boolean} autoSearchOnLoad - Whether to auto-search when criteria is present (for URL restore)
 * @param {Object} initialSearchCriteria - Optional initial search criteria (from URL params)
 * @returns {UsePowerInquiryResult}
 */
export const usePowerInquiry = (initialAgencyId, agencies = [], initialFilters = null, autoSearchOnLoad = false, initialSearchCriteria = null) => {
  // ============================================
  // STATE
  // ============================================

  // Selected agencies (multi-select support) - prefer URL agency IDs if provided
  const [selectedAgencyIds, setSelectedAgencyIds] = useState(() => {
    if (initialSearchCriteria?.agencyIds?.length > 0) {
      return initialSearchCriteria.agencyIds;
    }
    return initialAgencyId ? [initialAgencyId] : [];
  });

  // Search criteria - initialize from URL params if provided
  const [defendantLastName, setDefendantLastName] = useState(initialSearchCriteria?.defendantLastName || '');
  const [defendantFirstName, setDefendantFirstName] = useState(initialSearchCriteria?.defendantFirstName || '');
  const [powerNumber, setPowerNumber] = useState(initialSearchCriteria?.powerNumber || '');
  const [batchId, setBatchId] = useState(initialSearchCriteria?.batchId || '');
  const [reportNo, setReportNo] = useState(initialSearchCriteria?.reportNo || '');

  // Date filtering - initialize from URL params if provided
  const [dateType, setDateType] = useState(initialSearchCriteria?.dateType || DATE_TYPES.NONE);
  const [startDate, setStartDate] = useState(initialSearchCriteria?.startDate || '');
  const [endDate, setEndDate] = useState(initialSearchCriteria?.endDate || '');

  // Filters - initialize with values from navigation or URL if provided
  const [availableFilters, setAvailableFilters] = useState([]);
  const [selectedGroupFilter, setSelectedGroupFilter] = useState(
    initialSearchCriteria?.groupFilter || initialFilters?.groupFilter || 'AllPowers'
  );
  const [selectedSpecificFilters, setSelectedSpecificFilters] = useState(
    initialSearchCriteria?.specificFilters || initialFilters?.specificFilters || []
  );
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);

  // Track if we should auto-search on load (from dashboard with filters OR URL restore)
  const shouldAutoSearch = useRef(!!initialFilters || autoSearchOnLoad);

  // Results
  const [results, setResults] = useState([]);
  const [accumulatedResults, setAccumulatedResults] = useState([]); // For mobile infinite scroll
  const [totalCount, setTotalCount] = useState(0);
  const [totalBondAmount, setTotalBondAmount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false); // For infinite scroll loading state

  // Sorting
  const [sortBy, setSortBy] = useState(SORT_FIELDS.EXECUTION_DATE);
  const [sortDescending, setSortDescending] = useState(true);

  // Selected power for detail view
  const [selectedPower, setSelectedPower] = useState(null);
  const [isLoadingPowerDetail, setIsLoadingPowerDetail] = useState(false);

  // Loading states
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingAggregates, setIsLoadingAggregates] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Error state
  const [error, setError] = useState(null);

  // View mode - individual powers vs grouped by report
  const [viewMode, setViewMode] = useState(
    initialSearchCriteria?.viewMode || VIEW_MODES.INDIVIDUAL
  );

  // Grouped results (when viewMode is BY_REPORT)
  const [reportGroups, setReportGroups] = useState([]);
  const [totalReports, setTotalReports] = useState(0);
  const [expandedReports, setExpandedReports] = useState(new Set());
  const [loadingReportPowers, setLoadingReportPowers] = useState(new Set());
  const [reportPowersCache, setReportPowersCache] = useState({});

  // Track if initial search has been performed
  const initialSearchDone = useRef(false);

  // ============================================
  // DERIVED STATE
  // ============================================

  // Separate filters by type for UI rendering
  // RecalledPowers was added to the API by mistake — exclude it from the UI
  const { groupFilters, specificFilters, groupScopedFilters } = useMemo(() => {
    const visible = availableFilters.filter(f => f.filterName !== 'RecalledPowers');
    const group = visible.filter(f => f.filterType === FILTER_TYPES.GROUP);
    const specific = visible.filter(f => f.filterType === FILTER_TYPES.SPECIFIC);
    const groupScoped = visible.filter(f => f.filterType === FILTER_TYPES.GROUP_SCOPED);
    return {
      groupFilters: group.sort((a, b) => a.displayOrder - b.displayOrder),
      specificFilters: specific.sort((a, b) => a.displayOrder - b.displayOrder),
      groupScopedFilters: groupScoped.sort((a, b) => a.displayOrder - b.displayOrder),
    };
  }, [availableFilters]);

  // Derive the active group-scoped filter (e.g., AOL bucket) — at most one is selected
  const selectedGroupScopedFilter = useMemo(() => {
    const scopedNames = new Set(groupScopedFilters.map(f => f.filterName));
    return selectedSpecificFilters.find(f => scopedNames.has(f)) || null;
  }, [selectedSpecificFilters, groupScopedFilters]);

  // Build combined filters array for API request
  const activeFilters = useMemo(() => {
    const filters = [];

    // Add group filter if not "AllPowers"
    if (selectedGroupFilter && selectedGroupFilter !== 'AllPowers') {
      filters.push(selectedGroupFilter);
    }

    // Add specific filters
    filters.push(...selectedSpecificFilters);

    return filters;
  }, [selectedGroupFilter, selectedSpecificFilters]);

  // Check if search criteria has been entered
  const hasSearchCriteria = useMemo(() => {
    return (
      selectedAgencyIds.length > 0 &&
      (defendantLastName.trim() ||
        defendantFirstName.trim() ||
        powerNumber.trim() ||
        batchId ||
        reportNo.trim() ||
        activeFilters.length > 0 ||
        (dateType !== DATE_TYPES.NONE && (startDate || endDate)))
    );
  }, [
    selectedAgencyIds,
    defendantLastName,
    defendantFirstName,
    powerNumber,
    batchId,
    reportNo,
    activeFilters,
    dateType,
    startDate,
    endDate,
  ]);

  // ============================================
  // API OPERATIONS
  // ============================================

  /**
   * Load available filters from API
   */
  const loadFilters = useCallback(async () => {
    setIsLoadingFilters(true);
    try {
      const result = await getFilters();
      if (result.success) {
        setAvailableFilters(result.data || []);
      } else {
        console.error('Failed to load filters:', result.error);
        toast.error('Failed to load filter options');
      }
    } catch (err) {
      console.error('Load filters error:', err);
    } finally {
      setIsLoadingFilters(false);
    }
  }, []);

  /**
   * Build search request from current state
   * @param {number} page - Page number
   * @param {number} size - Page size
   */
  const buildSearchRequest = useCallback((page = pageNumber, size = pageSize) => {
    const request = {
      agencyIds: selectedAgencyIds,
      dateType,
      filters: activeFilters,
      pageNumber: page,
      pageSize: size,
      sortBy,
      sortDescending,
    };

    // Optional fields - only include if provided
    if (defendantLastName.trim()) {
      request.defendantLastName = defendantLastName.trim();
    }
    if (defendantFirstName.trim()) {
      request.defendantFirstName = defendantFirstName.trim();
    }
    if (powerNumber.trim()) {
      request.powerNumber = powerNumber.trim();
    }
    if (batchId) {
      request.batchId = parseInt(batchId, 10);
    }
    if (reportNo.trim()) {
      request.reportNo = reportNo.trim();
    }
    if (dateType !== DATE_TYPES.NONE) {
      if (startDate) {
        request.startDate = startDate;
      }
      if (endDate) {
        request.endDate = endDate;
      }
    }

    return request;
  }, [
    selectedAgencyIds,
    defendantLastName,
    defendantFirstName,
    powerNumber,
    batchId,
    reportNo,
    dateType,
    startDate,
    endDate,
    activeFilters,
    pageNumber,
    pageSize,
    sortBy,
    sortDescending,
  ]);

  /**
   * Execute search for individual powers with parallel loading for data and aggregates
   * @param {number} page - Page number (1 = first page, triggers parallel aggregates call)
   * @param {number} size - Page size
   * @param {boolean} appendResults - If true, append results instead of replacing (for infinite scroll)
   */
  const searchIndividual = useCallback(async (page = 1, size = pageSize, appendResults = false) => {
    const isFirstPage = page === 1;
    const request = buildSearchRequest(page, size);

    if (isFirstPage) {
      // Parallel loading: fetch data and aggregates simultaneously
      setIsLoadingAggregates(true);

      // Clear previous aggregate values immediately so stale data isn't shown
      setTotalCount(0);
      setTotalBondAmount(0);
      setTotalPages(0);
      // Reset accumulated results on fresh search
      setAccumulatedResults([]);

      // Start aggregates request (runs in background, updates when ready)
      getAggregates(request).then((aggregatesResult) => {
        if (aggregatesResult.success && aggregatesResult.data) {
          const count = aggregatesResult.data.totalCount || 0;
          const calculatedTotalPages = Math.ceil(count / size);
          setTotalCount(count);
          setTotalBondAmount(aggregatesResult.data.totalBondAmount || 0);
          setTotalPages(calculatedTotalPages);
          // Update hasNextPage now that we know the total - page 1 has next if totalPages > 1
          setHasNextPage(calculatedTotalPages > 1);
        } else {
          setTotalCount(0);
          setTotalBondAmount(0);
          setTotalPages(0);
          setHasNextPage(false);
          console.error('Failed to load aggregates:', aggregatesResult.error);
        }
        setIsLoadingAggregates(false);
      }).catch((err) => {
        console.error('Aggregates error:', err);
        setTotalCount(0);
        setTotalBondAmount(0);
        setTotalPages(0);
        setHasNextPage(false);
        setIsLoadingAggregates(false);
      });

      // Fetch data (awaited - shows results as soon as ready)
      const dataResult = await searchPowers(request);

      if (dataResult.success && dataResult.data) {
        const paginatedData = dataResult.data.results || dataResult.data;
        const newItems = paginatedData.items || [];
        setResults(newItems);
        setAccumulatedResults(newItems); // Initialize accumulated results
        setPageNumber(paginatedData.pageNumber || page);
        // Use API response if available, otherwise assume there's more if we got a full page
        // This will be corrected when aggregates complete with actual totalPages
        const apiHasNextPage = paginatedData.hasNextPage;
        setHasNextPage(apiHasNextPage !== undefined ? apiHasNextPage : newItems.length >= size);
        setHasPreviousPage(paginatedData.hasPreviousPage || false);
      } else {
        setError(dataResult.error || 'Search failed');
        setResults([]);
        setAccumulatedResults([]);
        toast.error(dataResult.error || 'Search failed');
      }
    } else {
      // Page 2+: only fetch data, use cached aggregates
      const result = await searchPowers(request);

      if (result.success && result.data) {
        const paginatedData = result.data.results || result.data;
        const newItems = paginatedData.items || [];
        const currentPage = paginatedData.pageNumber || page;
        setResults(newItems);
        // Append to accumulated results if in infinite scroll mode
        if (appendResults) {
          setAccumulatedResults(prev => [...prev, ...newItems]);
        }
        setPageNumber(currentPage);
        // Calculate hasNextPage based on current page and totalPages (already set from first page)
        setHasNextPage(currentPage < totalPages);
        setHasPreviousPage(currentPage > 1);
      } else {
        setError(result.error || 'Search failed');
        setResults([]);
        toast.error(result.error || 'Search failed');
      }
    }
  }, [buildSearchRequest, pageSize, totalPages]);

  /**
   * Execute search for grouped by report view
   * @param {number} page - Page number
   * @param {number} size - Page size (number of reports per page)
   */
  const searchByReport = useCallback(async (page = 1, size = pageSize) => {
    const request = buildSearchRequest(page, size);
    const isFirstPage = page === 1;

    // Clear expanded reports and cache when doing a new search
    if (isFirstPage) {
      setExpandedReports(new Set());
      setReportPowersCache({});
      setTotalCount(0);
      setTotalBondAmount(0);
      setTotalPages(0);

      // Fetch aggregates in parallel (same as individual view)
      setIsLoadingAggregates(true);
      getAggregates(request).then((aggregatesResult) => {
        if (aggregatesResult.success && aggregatesResult.data) {
          setTotalCount(aggregatesResult.data.totalCount || 0);
          setTotalBondAmount(aggregatesResult.data.totalBondAmount || 0);
        } else {
          setTotalCount(0);
          setTotalBondAmount(0);
          console.error('Failed to load aggregates:', aggregatesResult.error);
        }
        setIsLoadingAggregates(false);
      }).catch((err) => {
        console.error('Aggregates error:', err);
        setTotalCount(0);
        setTotalBondAmount(0);
        setIsLoadingAggregates(false);
      });
    }

    const result = await searchPowersByReport(request);

    if (result.success && result.data) {
      const paginatedData = result.data.results || result.data;
      setReportGroups(paginatedData.items || []);
      setTotalReports(paginatedData.totalCount || 0);
      setPageNumber(paginatedData.pageNumber || page);
      setTotalPages(paginatedData.totalPages || 0);
      setHasNextPage(paginatedData.hasNextPage || false);
      setHasPreviousPage(paginatedData.hasPreviousPage || false);
    } else {
      setError(result.error || 'Search failed');
      setReportGroups([]);
      toast.error(result.error || 'Search failed');
    }
  }, [buildSearchRequest, pageSize]);

  /**
   * Execute search with parallel loading for data and aggregates
   * Branches based on current viewMode
   * @param {number} page - Page number (1 = first page, triggers parallel aggregates call)
   * @param {number} size - Page size
   */
  const search = useCallback(async (page = 1, size = pageSize) => {
    if (selectedAgencyIds.length === 0) {
      toast.warning('Please select at least one agency');
      return;
    }

    setIsSearching(true);
    setError(null);
    setHasSearched(true);

    try {
      if (viewMode === VIEW_MODES.BY_REPORT) {
        await searchByReport(page, size);
      } else {
        await searchIndividual(page, size);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Network error');
      toast.error('Network error occurred');
    } finally {
      setIsSearching(false);
    }
  }, [selectedAgencyIds, viewMode, searchIndividual, searchByReport, pageSize]);

  /**
   * Handle pagination change
   */
  const handlePageChange = useCallback((newPage) => {
    search(newPage, pageSize);
  }, [search, pageSize]);

  /**
   * Load more results for infinite scroll (mobile)
   * Appends results to the accumulated list
   */
  const loadMore = useCallback(async () => {
    // Guard conditions - return early if we shouldn't load more
    if (!hasNextPage || isSearching || isLoadingMore) {
      return;
    }

    setIsLoadingMore(true);
    try {
      const nextPage = pageNumber + 1;
      const request = buildSearchRequest(nextPage, pageSize);

      // Call the API to get the next page
      const result = await searchPowers(request);

      if (result.success && result.data) {
        const paginatedData = result.data.results || result.data;
        const newItems = paginatedData.items || [];
        const currentPage = paginatedData.pageNumber || nextPage;
        setResults(newItems);
        setAccumulatedResults(prev => [...prev, ...newItems]);
        setPageNumber(currentPage);
        // Calculate hasNextPage based on current page and total pages we already know
        setHasNextPage(currentPage < totalPages);
        setHasPreviousPage(currentPage > 1);
      } else {
        toast.error(result.error || 'Failed to load more results');
      }
    } catch (err) {
      console.error('Load more error:', err);
      toast.error('Network error occurred');
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasNextPage, isSearching, isLoadingMore, pageNumber, pageSize, totalPages, buildSearchRequest, searchPowers]);

  /**
   * Handle page size change
   */
  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    search(1, newSize);
  }, [search]);

  /**
   * Handle sort change
   */
  const handleSortChange = useCallback((field, descending = true) => {
    setSortBy(field);
    setSortDescending(descending);
    // Trigger search with new sort (after state update)
  }, []);

  /**
   * Load power details
   */
  const loadPowerDetails = useCallback(async (powerNo) => {
    setIsLoadingPowerDetail(true);
    try {
      const result = await getPowerDetails(powerNo);
      if (result.success && result.data) {
        setSelectedPower(result.data);
        return result.data;
      } else {
        toast.error(result.error || 'Failed to load power details');
        return null;
      }
    } catch (err) {
      console.error('Load power details error:', err);
      toast.error('Network error');
      return null;
    } finally {
      setIsLoadingPowerDetail(false);
    }
  }, []);

  /**
   * Close power detail view
   */
  const closePowerDetail = useCallback(() => {
    setSelectedPower(null);
  }, []);

  /**
   * Export results to CSV
   */
  const exportToCsv = useCallback(async () => {
    if (selectedAgencyIds.length === 0) {
      toast.warning('Please select at least one agency');
      return;
    }

    setIsExporting(true);

    try {
      const request = buildSearchRequest(1);
      delete request.pageSize;
      delete request.pageNumber;
      const result = await exportPowersCsv(request);

      if (result.success && result.blob) {
        const timestamp = new Date().toISOString().slice(0, 10);
        downloadBlob(result.blob, `power-inquiry-${timestamp}.csv`);
        toast.success('Export completed');
      } else {
        toast.error(result.error || 'Export failed');
      }
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Network error occurred');
    } finally {
      setIsExporting(false);
    }
  }, [selectedAgencyIds, buildSearchRequest]);

  /**
   * Clear all search criteria
   */
  const clearSearch = useCallback(() => {
    // Clear search criteria only - results remain visible
    setDefendantLastName('');
    setDefendantFirstName('');
    setPowerNumber('');
    setBatchId('');
    setReportNo('');
    setDateType(DATE_TYPES.NONE);
    setStartDate('');
    setEndDate('');
    setSelectedGroupFilter('AllPowers');
    setSelectedSpecificFilters([]);
    setAccumulatedResults([]);
    setError(null);
  }, []);

  // ============================================
  // FILTER HANDLERS
  // ============================================

  /**
   * Handle group filter change (radio button - single selection)
   * Clicking an already-selected filter will toggle it off (back to AllPowers)
   * Also clears any group-scoped filters since they depend on the group selection
   */
  const handleGroupFilterChange = useCallback((filterName) => {
    setSelectedGroupFilter((prev) => (prev === filterName ? 'AllPowers' : filterName));
    // Clear group-scoped filters (e.g., AOL buckets) — they require a specific group filter
    const scopedNames = new Set(groupScopedFilters.map(f => f.filterName));
    if (scopedNames.size > 0) {
      setSelectedSpecificFilters(prev => prev.filter(f => !scopedNames.has(f)));
    }
  }, [groupScopedFilters]);

  /**
   * Handle group-scoped filter change (radio button - mutually exclusive within scope)
   * e.g., AOL bucket filters — only one can be active at a time
   * Selecting the already-active filter deselects it
   */
  const handleGroupScopedFilterChange = useCallback((filterName) => {
    const scopedNames = new Set(groupScopedFilters.map(f => f.filterName));
    setSelectedSpecificFilters(prev => {
      // Remove any existing group-scoped filter
      const withoutScoped = prev.filter(f => !scopedNames.has(f));
      // null = "All" (clear scoped filter), or toggle off if same filter clicked
      if (!filterName || prev.includes(filterName)) {
        return withoutScoped;
      }
      return [...withoutScoped, filterName];
    });
  }, [groupScopedFilters]);

  /**
   * Handle specific filter toggle (checkbox - multiple selection)
   */
  const handleSpecificFilterToggle = useCallback((filterName) => {
    setSelectedSpecificFilters(prev => {
      if (prev.includes(filterName)) {
        return prev.filter(f => f !== filterName);
      }
      return [...prev, filterName];
    });
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setSelectedGroupFilter('AllPowers');
    setSelectedSpecificFilters([]);
  }, []);

  // ============================================
  // VIEW MODE HANDLERS
  // ============================================

  /**
   * Handle view mode change
   * Clears results when switching modes
   */
  const handleViewModeChange = useCallback(async (newMode) => {
    if (newMode !== viewMode) {
      const hadResults = hasSearched;
      setViewMode(newMode);
      // Clear results when switching modes
      setResults([]);
      setAccumulatedResults([]);
      setReportGroups([]);
      setExpandedReports(new Set());
      setReportPowersCache({});
      setTotalCount(0);
      setTotalBondAmount(0);
      setTotalReports(0);
      // Auto-search in the new view mode if results were already showing
      if (hadResults && selectedAgencyIds.length > 0) {
        setIsSearching(true);
        setError(null);
        setHasSearched(true);
        try {
          if (newMode === VIEW_MODES.BY_REPORT) {
            await searchByReport(1);
          } else {
            await searchIndividual(1);
          }
        } catch (err) {
          console.error('Search error:', err);
          setError('Network error');
          toast.error('Network error occurred');
        } finally {
          setIsSearching(false);
        }
      } else {
        setHasSearched(false);
      }
    }
  }, [viewMode, hasSearched, selectedAgencyIds, searchByReport, searchIndividual]);

  /**
   * Toggle report row expansion
   * @param {string} reportNo - Report number to toggle
   */
  const toggleReportExpanded = useCallback((reportNo) => {
    setExpandedReports(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reportNo)) {
        newSet.delete(reportNo);
      } else {
        newSet.add(reportNo);
      }
      return newSet;
    });
  }, []);

  /**
   * Load powers for an expanded report (lazy loading)
   * Applies the same filters as the current search to show only matching powers
   * @param {string} reportNo - Report number to load powers for
   */
  const loadReportPowers = useCallback(async (reportNo) => {
    // Check if already cached
    if (reportPowersCache[reportNo]) {
      return reportPowersCache[reportNo];
    }

    // Check if already loading
    if (loadingReportPowers.has(reportNo)) {
      return null;
    }

    // Mark as loading
    setLoadingReportPowers(prev => new Set(prev).add(reportNo));

    try {
      // Build request with current filters, then override for this specific report
      const baseRequest = buildSearchRequest(1, 1000);
      const request = {
        ...baseRequest,
        pageNumber: 1,
        pageSize: 1000, // Get all powers for this report
      };
      // Null/empty reportNo means unreported powers — use hasReport: false
      // Otherwise filter by the specific report number
      if (reportNo) {
        request.reportNo = reportNo;
      } else {
        request.hasReport = false;
      }

      const result = await searchPowers(request);

      if (result.success && result.data) {
        const paginatedData = result.data.results || result.data;
        const powers = paginatedData.items || [];

        // Cache the results
        setReportPowersCache(prev => ({
          ...prev,
          [reportNo]: powers,
        }));

        return powers;
      } else {
        toast.error(`Failed to load powers for report ${reportNo}`);
        return [];
      }
    } catch (err) {
      console.error('Load report powers error:', err);
      toast.error('Network error loading powers');
      return [];
    } finally {
      setLoadingReportPowers(prev => {
        const newSet = new Set(prev);
        newSet.delete(reportNo);
        return newSet;
      });
    }
  }, [buildSearchRequest, reportPowersCache, loadingReportPowers]);

  // ============================================
  // AGENCY HANDLERS
  // ============================================

  /**
   * Toggle agency selection
   */
  const toggleAgency = useCallback((agencyId) => {
    setSelectedAgencyIds(prev => {
      if (prev.includes(agencyId)) {
        return prev.filter(id => id !== agencyId);
      }
      return [...prev, agencyId];
    });
  }, []);

  /**
   * Select all agencies
   */
  const selectAllAgencies = useCallback(() => {
    setSelectedAgencyIds(agencies.map(a => a.agencyId));
  }, [agencies]);

  /**
   * Clear agency selection
   */
  const clearAgencySelection = useCallback(() => {
    setSelectedAgencyIds([]);
  }, []);

  // ============================================
  // EFFECTS
  // ============================================

  // Load filters on mount
  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  // Set initial agency when agencies load
  useEffect(() => {
    if (agencies.length > 0 && selectedAgencyIds.length === 0) {
      setSelectedAgencyIds([agencies[0].agencyId]);
    }
  }, [agencies, selectedAgencyIds.length]);

  // Sync date fields when URL params change (navigation from Dashboard with date filters)
  useEffect(() => {
    if (initialSearchCriteria) {
      if (initialSearchCriteria.dateType && initialSearchCriteria.dateType !== DATE_TYPES.NONE) {
        setDateType(initialSearchCriteria.dateType);
      }
      if (initialSearchCriteria.startDate) {
        setStartDate(initialSearchCriteria.startDate);
      }
      if (initialSearchCriteria.endDate) {
        setEndDate(initialSearchCriteria.endDate);
      }
    }
  }, [initialSearchCriteria]);

  // Auto-search when initial filters/criteria are provided (from Dashboard or URL restore)
  useEffect(() => {
    if (
      shouldAutoSearch.current &&
      selectedAgencyIds.length > 0 &&
      !initialSearchDone.current &&
      !isLoadingFilters
    ) {
      initialSearchDone.current = true;
      search(1);
    }
  }, [selectedAgencyIds, isLoadingFilters, search, initialFilters, selectedGroupFilter, selectedSpecificFilters, activeFilters, autoSearchOnLoad]);

  // ============================================
  // RETURN
  // ============================================

  return {
    // Agency selection
    selectedAgencyIds,
    setSelectedAgencyIds,
    toggleAgency,
    selectAllAgencies,
    clearAgencySelection,

    // Search criteria
    defendantLastName,
    setDefendantLastName,
    defendantFirstName,
    setDefendantFirstName,
    powerNumber,
    setPowerNumber,
    batchId,
    setBatchId,
    reportNo,
    setReportNo,

    // Date filtering
    dateType,
    setDateType,
    startDate,
    setStartDate,
    endDate,
    setEndDate,

    // Filters
    availableFilters,
    groupFilters,
    specificFilters,
    selectedGroupFilter,
    selectedSpecificFilters,
    setSelectedGroupFilter,
    setSelectedSpecificFilters,
    activeFilters,
    isLoadingFilters,
    handleGroupFilterChange,
    handleSpecificFilterToggle,
    groupScopedFilters,
    selectedGroupScopedFilter,
    handleGroupScopedFilterChange,
    clearFilters,

    // View mode
    viewMode,
    setViewMode,
    handleViewModeChange,

    // Results (individual powers)
    results,
    accumulatedResults, // For mobile infinite scroll
    totalCount,
    totalBondAmount,
    pageNumber,
    pageSize,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    isLoadingMore, // For infinite scroll loading state
    loadMore, // Function to load more results (infinite scroll)

    // Results (grouped by report)
    reportGroups,
    totalReports,
    expandedReports,
    loadingReportPowers,
    reportPowersCache,
    toggleReportExpanded,
    loadReportPowers,

    // Sorting
    sortBy,
    sortDescending,
    handleSortChange,

    // Power detail
    selectedPower,
    isLoadingPowerDetail,
    loadPowerDetails,
    closePowerDetail,

    // Loading states
    isSearching,
    isLoadingAggregates,
    isExporting,
    hasSearched,
    hasSearchCriteria,

    // Error
    error,

    // Actions
    search,
    handlePageChange,
    handlePageSizeChange,
    exportToCsv,
    clearSearch,
    loadFilters,
  };
};

/**
 * @typedef {Object} UsePowerInquiryResult
 * @property {number[]} selectedAgencyIds
 * @property {Function} setSelectedAgencyIds
 * @property {Function} toggleAgency
 * @property {Function} selectAllAgencies
 * @property {Function} clearAgencySelection
 * @property {string} defendantLastName
 * @property {Function} setDefendantLastName
 * @property {string} defendantFirstName
 * @property {Function} setDefendantFirstName
 * @property {string} powerNumber
 * @property {Function} setPowerNumber
 * @property {string} batchId
 * @property {Function} setBatchId
 * @property {string} reportNo
 * @property {Function} setReportNo
 * @property {string} dateType
 * @property {Function} setDateType
 * @property {string} startDate
 * @property {Function} setStartDate
 * @property {string} endDate
 * @property {Function} setEndDate
 * @property {Array} availableFilters
 * @property {Array} groupFilters
 * @property {Array} specificFilters
 * @property {string} selectedGroupFilter
 * @property {string[]} selectedSpecificFilters
 * @property {string[]} activeFilters
 * @property {boolean} isLoadingFilters
 * @property {Function} handleGroupFilterChange
 * @property {Function} handleSpecificFilterToggle
 * @property {Function} clearFilters
 * @property {Array} results
 * @property {number} totalCount
 * @property {number} pageNumber
 * @property {number} pageSize
 * @property {number} totalPages
 * @property {boolean} hasMore
 * @property {boolean} isSearching
 * @property {boolean} isExporting
 * @property {boolean} hasSearched
 * @property {boolean} hasSearchCriteria
 * @property {string|null} error
 * @property {Function} search
 * @property {Function} handlePageChange
 * @property {Function} handlePageSizeChange
 * @property {Function} exportToCsv
 * @property {Function} clearSearch
 * @property {Function} loadFilters
 */

export default usePowerInquiry;
